import { getLegalActions } from "@fate-kingdom/engine";
import { useEffect, useMemo, useState } from "react";

import { DeveloperBattlefield3D } from "../battlefield-3d/DeveloperBattlefield3D";
import { AlphaApp } from "../alpha/AlphaApp";
import { useAlphaGameStore } from "../alpha/alpha-game-store";
import { ArmyDeploymentPanel } from "../hud/ArmyDeploymentPanel";
import { CommanderDeploymentPanel } from "../hud/CommanderDeploymentPanel";
import { GameHud } from "../hud/GameHud";
import { DeveloperDiagnostics } from "../hud/DeveloperDiagnostics";
import { HandPanel } from "../hud/HandPanel";
import { LaneStatusStrip } from "../hud/LaneStatusStrip";
import { SupplyChoicePanel } from "../hud/SupplyChoicePanel";
import { CommandDisbandPanel } from "../hud/CommandDisbandPanel";
import { useReducedMotion } from "../hooks/useReducedMotion";
import {
  toArmyDeploymentPreview,
  toCommanderPlacementPreview,
  toMatchViewModel,
} from "../controller/toMatchViewModel";
import { useGameStore } from "../stores/gameStore";
import "../styles/app.css";

function DeveloperApp({ onExit }: { onExit: () => void }) {
  const gameState = useGameStore((state) => state.game);
  const selectedLaneId = useGameStore((state) => state.selectedLaneId);
  const selectedCardInstanceId = useGameStore((state) => state.selectedCardInstanceId);
  const selectedSupplyArmyInstanceId = useGameStore((state) => state.selectedSupplyArmyInstanceId);
  const selectedCommandCardInstanceId = useGameStore(
    (state) => state.selectedCommandCardInstanceId,
  );
  const pendingCommanderActionType = useGameStore((state) => state.pendingCommanderActionType);
  const lastCommandDisbandTrace = useGameStore((state) => state.lastCommandDisbandTrace);
  const lastCommanderPlacementTrace = useGameStore((state) => state.lastCommanderPlacementTrace);
  const lastArmyDeploymentTrace = useGameStore((state) => state.lastArmyDeploymentTrace);
  const lastEvents = useGameStore((state) => state.lastEvents);
  const fixtureMode = useGameStore((state) => state.fixtureMode);
  const validationMessage = useGameStore((state) => state.validationMessage);
  const contextStatus = useGameStore((state) => state.contextStatus);
  const selectLane = useGameStore((state) => state.selectLane);
  const selectCard = useGameStore((state) => state.selectCard);
  const cancelSelection = useGameStore((state) => state.cancelSelection);
  const selectSupplyArmy = useGameStore((state) => state.selectSupplyArmy);
  const confirmSupplyDiscard = useGameStore((state) => state.confirmSupplyDiscard);
  const selectCommandDisbandCard = useGameStore((state) => state.selectCommandDisbandCard);
  const confirmCommandDisband = useGameStore((state) => state.confirmCommandDisband);
  const cancelCommandDisband = useGameStore((state) => state.cancelCommandDisband);
  const confirmCommanderPlacement = useGameStore((state) => state.confirmCommanderPlacement);
  const cancelCommanderPlacement = useGameStore((state) => state.cancelCommanderPlacement);
  const confirmArmyDeployment = useGameStore((state) => state.confirmArmyDeployment);
  const cancelArmyDeployment = useGameStore((state) => state.cancelArmyDeployment);
  const attackSelectedLane = useGameStore((state) => state.attackSelectedLane);
  const advancePhase = useGameStore((state) => state.advancePhase);
  const reset = useGameStore((state) => state.reset);
  const loadFixture = useGameStore((state) => state.loadFixture);
  const setContextStatus = useGameStore((state) => state.setContextStatus);
  const reducedMotion = useReducedMotion();

  const game = useMemo(() => toMatchViewModel(gameState), [gameState]);
  const legalActions = useMemo(
    () => getLegalActions(gameState, gameState.activePlayerId),
    [gameState],
  );
  const canAttack =
    selectedLaneId !== undefined &&
    legalActions.some((action) => action.type === "attack" && action.laneId === selectedLaneId);
  const canAdvancePhase = legalActions.some((action) => action.type === "advance-phase");
  const selectedCard = game.activeHand.find((card) => card.instanceId === selectedCardInstanceId);
  const selectedCommander = selectedCard?.commander;
  const selectedArmy = selectedCard?.army;
  const supplyBlocked = game.supply.status === "awaiting-discard";
  const commanderPreview = useMemo(
    () =>
      selectedCardInstanceId !== undefined &&
      selectedLaneId !== undefined &&
      pendingCommanderActionType !== undefined
        ? toCommanderPlacementPreview(
            gameState,
            selectedCardInstanceId,
            selectedLaneId,
            pendingCommanderActionType,
          )
        : undefined,
    [gameState, pendingCommanderActionType, selectedCardInstanceId, selectedLaneId],
  );
  const commanderSelectionActive = selectedCommander !== undefined;
  const commanderPreviewOpen = commanderPreview?.legal === true;
  const armySelectionActive = selectedArmy !== undefined;
  const armyPreview = useMemo(
    () =>
      armySelectionActive && selectedCardInstanceId !== undefined && selectedLaneId !== undefined
        ? toArmyDeploymentPreview(gameState, selectedCardInstanceId, selectedLaneId)
        : undefined,
    [armySelectionActive, gameState, selectedCardInstanceId, selectedLaneId],
  );
  const armyPreviewOpen = armyPreview?.legal === true;
  const commandDisbandPanelOpen =
    game.phase === "command" &&
    selectedLaneId !== undefined &&
    !commanderSelectionActive &&
    !armySelectionActive &&
    pendingCommanderActionType === undefined;
  const commandPanelOpen = commandDisbandPanelOpen || commanderPreviewOpen || armyPreviewOpen;
  const lowerChoiceOpen = supplyBlocked || commandPanelOpen;
  const legalLandTargetIds =
    selectedCard?.type === "land"
      ? game.lanes
          .filter((lane) =>
            legalActions.some(
              (action) =>
                (action.type === "found-land" || action.type === "develop-land") &&
                action.cardInstanceId === selectedCardInstanceId &&
                action.laneId === lane.laneId,
            ),
          )
          .map((lane) => lane.laneId)
      : [];
  const legalCommanderTargetIds = commanderSelectionActive
    ? selectedCommander.targets.filter((target) => target.legal).map((target) => target.laneId)
    : [];
  const legalArmyTargetIds = armySelectionActive
    ? selectedArmy.targets.filter((target) => target.legal).map((target) => target.laneId)
    : [];
  const legalTargetIds = [
    ...new Set([...legalLandTargetIds, ...legalCommanderTargetIds, ...legalArmyTargetIds]),
  ];

  return (
    <main
      className="game-shell"
      data-testid="game-shell"
      data-context-status={contextStatus}
      data-fixture-mode={fixtureMode}
      data-has-hand={game.activeHand.length > 0 && !lowerChoiceOpen}
      data-supply-status={game.supply.status}
      data-command-status={game.command.status}
      data-command-panel-open={commandPanelOpen}
      data-commander-preview-open={commanderPreviewOpen}
      data-army-preview-open={armyPreviewOpen}
      data-target-selection-active={selectedCard !== undefined}
      data-fixture-boundary={
        fixtureMode === "army-deployment" || fixtureMode === "cleanup"
          ? "supplied-state"
          : "standard"
      }
    >
      <div className="battlefield-layer">
        <DeveloperBattlefield3D
          lanes={game.lanes}
          selectedLaneId={selectedLaneId}
          legalTargetIds={legalTargetIds}
          reducedMotion={reducedMotion}
          onSelectLane={selectLane}
          onContextStatusChange={setContextStatus}
        />
      </div>

      <button className="developer-mode-exit" type="button" onClick={onExit}>
        Player Mode
      </button>

      <p className="foundation-caption">
        {fixtureMode === "army-deployment"
          ? "Army Deployment supplied-state boundary · Lands, Commanders and existing garrisons staged before real Supply · test-only cards"
          : "Deterministic setup, Land, Supply, Command and combat fixtures · test-only cards · incomplete match"}
      </p>

      <LaneStatusStrip
        lanes={game.lanes}
        selectedLaneId={selectedLaneId}
        legalLandTargetIds={legalLandTargetIds}
        landSelectionActive={selectedCard?.type === "land"}
        supply={game.supply}
        command={game.command}
        commanderTargets={selectedCommander?.targets ?? []}
        commanderSelectionActive={commanderSelectionActive}
        armyTargets={selectedArmy?.targets ?? []}
        armySelectionActive={armySelectionActive}
        onSelectLane={selectLane}
      />

      {supplyBlocked ? (
        <SupplyChoicePanel
          supply={game.supply}
          selectedLaneId={selectedLaneId}
          selectedArmyInstanceId={selectedSupplyArmyInstanceId}
          validationMessage={validationMessage}
          onSelectArmy={selectSupplyArmy}
          onConfirmDiscard={confirmSupplyDiscard}
          onCancel={cancelSelection}
        />
      ) : commanderPreviewOpen &&
        commanderPreview !== undefined &&
        selectedCard !== undefined &&
        selectedCommander !== undefined ? (
        <CommanderDeploymentPanel
          commanderName={selectedCard.name}
          commander={selectedCommander}
          target={commanderPreview}
          validationMessage={validationMessage}
          onConfirm={confirmCommanderPlacement}
          onCancel={cancelCommanderPlacement}
        />
      ) : armyPreviewOpen &&
        armyPreview !== undefined &&
        selectedCard !== undefined &&
        selectedArmy !== undefined ? (
        <ArmyDeploymentPanel
          armyName={selectedCard.name}
          army={selectedArmy}
          target={armyPreview}
          validationMessage={validationMessage}
          onConfirm={confirmArmyDeployment}
          onCancel={cancelArmyDeployment}
        />
      ) : commandDisbandPanelOpen && selectedLaneId !== undefined ? (
        <CommandDisbandPanel
          command={game.command}
          eventLimit={game.eventLimit}
          selectedLaneId={selectedLaneId}
          selectedCardInstanceId={selectedCommandCardInstanceId}
          validationMessage={validationMessage}
          onSelectCard={selectCommandDisbandCard}
          onConfirm={confirmCommandDisband}
          onCancel={cancelCommandDisband}
        />
      ) : (
        <HandPanel
          activePlayerId={game.activePlayerId}
          cards={game.activeHand}
          selectedCardInstanceId={selectedCardInstanceId}
          validationMessage={validationMessage}
          onSelectCard={selectCard}
          onCancelSelection={cancelSelection}
        />
      )}

      <DeveloperDiagnostics
        game={game}
        selectedLaneId={selectedLaneId}
        selectedCommandCardInstanceId={selectedCommandCardInstanceId}
        selectedCommanderCardInstanceId={
          selectedCommander === undefined ? undefined : selectedCardInstanceId
        }
        pendingCommanderActionType={pendingCommanderActionType}
        commanderPreview={commanderPreview}
        lastCommandDisbandTrace={lastCommandDisbandTrace}
        lastCommanderPlacementTrace={lastCommanderPlacementTrace}
        selectedArmyCardInstanceId={selectedArmy === undefined ? undefined : selectedCardInstanceId}
        armyPreview={armyPreview}
        lastArmyDeploymentTrace={lastArmyDeploymentTrace}
        lastEvents={lastEvents}
      />

      <GameHud
        game={game}
        fixtureMode={fixtureMode}
        selectedLaneId={selectedLaneId}
        selectedCardName={selectedCard?.name}
        canAttack={canAttack}
        canAdvancePhase={canAdvancePhase}
        supplyBlocked={supplyBlocked}
        commandPanelOpen={commandPanelOpen}
        lastEvents={lastEvents}
        validationMessage={validationMessage}
        onAttack={attackSelectedLane}
        onAdvancePhase={advancePhase}
        onReset={reset}
        onFixtureChange={loadFixture}
      />

      {contextStatus === "lost" ? (
        <div className="context-alert" role="alert">
          The 3D battlefield paused after losing its graphics context. Your deterministic match
          state is safe and will be redrawn when the browser restores WebGL.
        </div>
      ) : null}
    </main>
  );
}

function initialMode(): "player" | "developer" {
  return new URLSearchParams(window.location.search).get("mode") === "developer"
    ? "developer"
    : "player";
}

/** Player Mode is deliberately separate from the fixture-oriented Developer Mode. */
export function App() {
  const [mode, setMode] = useState<"player" | "developer">(initialMode);
  const leaveAlpha = useAlphaGameStore((state) => state.leave);

  useEffect(() => {
    if (import.meta.env.MODE !== "test") return;
    let active = true;
    void import("./test-bootstrap").then(({ installRequestedTestState }) => {
      if (active) installRequestedTestState();
    });
    return () => {
      active = false;
    };
  }, []);

  return mode === "developer" ? (
    <DeveloperApp onExit={() => setMode("player")} />
  ) : (
    <AlphaApp
      onOpenDeveloperMode={() => {
        leaveAlpha();
        setMode("developer");
      }}
    />
  );
}
